import { useMemo } from "react";
import { parseCmsId, useGetCmsIssuers } from "api/cms";

/**
 * One contactable party's identity: the id we select and send with, plus what
 * we show for it.
 *
 * This is the ONLY place in the contact flow that knows where identity comes
 * from, and the only place that decides who is contactable at all.
 *
 * Identity comes from the CMS issuer list (`GET /api/v1/Issuers`). There is no
 * dedicated issuer-contact route to move to: the CMS send route
 * (`POST /api/v1/Email`) only accepts recipients, it does not describe them.
 *
 * The `hasContact` filter lives here rather than in `useGetCmsIssuers` on
 * purpose — the issuers listing page shares that query, and filtering at the
 * query level would shrink the public listing to the contactable few.
 */
export interface DirectoryEntry {
  /** Portal-side issuer id, as a string so int64 CMS ids join safely. */
  externalId: string;
  name: string;
  logoUrl?: string;
}

const toEntry = (issuer: {
  externalId?: number | string | null;
  name?: string | null;
  logoImage?: string | null;
  hasContact?: boolean;
}): DirectoryEntry | null => {
  // `hasContact` is the CMS's own answer to "can this issuer be mailed via
  // POST /api/v1/Email". Being in the directory is what makes an issuer
  // offerable (see the inner join in useRelevantIssuers), so an unflagged
  // issuer is simply never shown rather than shown and failing on send.
  if (!issuer.hasContact) return null;
  const externalId = parseCmsId(issuer.externalId);
  if (externalId === null || !issuer.name) return null;
  return {
    externalId: String(externalId),
    name: issuer.name,
    logoUrl: issuer.logoImage ?? undefined,
  };
};

export const useIssuerDirectory = () => {
  const { loading, error, data } = useGetCmsIssuers();

  const entries = useMemo<DirectoryEntry[]>(() => {
    if (!data) return [];
    // Membership is decided by `hasContact` in `toEntry`.
    //
    // `hasContact` and `contacts[]` are unrelated fields — confirmed with the
    // CMS owner, and do not reason from one to the other. Live data shows both
    // mismatches: Handelsbanken is flagged true with an empty `contacts[]`,
    // while five issuers carry named contacts with emails and are flagged
    // false. So the address this route mails is not necessarily one we can
    // see, and `contacts.some(c => c.email)` is NOT a substitute gate — it
    // would hide a mailable issuer and offer five unmailable ones.
    //
    // `contacts[]` is display data for the issuer page; `hasContact` is the
    // only answer to "can we mail this issuer". Expect the grid to be much
    // smaller than the 43-row issuer list.
    return data
      .map(toEntry)
      .filter((entry): entry is DirectoryEntry => entry !== null);
  }, [data]);

  return { data: entries, loading, error };
};
